<template>
  <section v-if="isGetRequestPending" class="edit-prompt">
    <p-loading />
  </section>
  <section v-else class="edit-prompt">
    <header>
      <h1>Edit Chain</h1>
      <div class="action-wrapper">
        <p-button v-if="$hasPermission('prompts.write')" color="secondary" @click="confirmDelete">Delete</p-button>
      </div>
    </header>
    <p-edit-prompt-form
      :item="item"
      :is-request-pending="isUpdateRequestPending"
      :is-request-failed="isUpdateRequestFailed"
      @submit="submit"
      @cancel="$router.back()"
    />
    <footer></footer>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import Loading from '@/components/common/Loading';
import Button from '@/components/common/Button';
import EditForm from './_CreateOrEdit';

export default {
  components: {
    'p-loading': Loading,
    'p-button': Button,
    'p-edit-prompt-form': EditForm
  },
  computed: {
    ...mapState({
      item: s => s.prompts.item,
      draft: s => s.prompts.draft,
      isGetRequestPending: s => s.prompts.isGetRequestPending,
      isUpdateRequestPending: s => s.prompts.isUpdateRequestPending,
      isUpdateRequestFailed: s => s.prompts.isUpdateRequestFailed
    })
  },
  watch: {
    '$route.params': {
      handler() {
        Promise.all([
          this.$store.dispatch('prompts/getById', this.$route.params.id),
          this.$store.dispatch('prompts/loadDraft', this.$route.params.id)
        ]);
      },
      immediate: true
    }
  },
  methods: {
    async submit(props) {
      const { template, ...rest } = props;

      const confirm = await this.$confirm({
        title: `Save new version?`,
        message: `Are you sure you want to save a new version?`,
        confirm: 'Save',
        userResponseLabel: 'Comment'
      });

      if (!confirm) {
        return;
      }

      await Promise.all([
        this.$store.dispatch('prompts/update', {
          id: this.$route.params.id,
          ...props,
          comment: confirm.userResponse
        }),
        this.draft
          ? await this.$store.dispatch('prompts/saveDraft', {
              id: this.$route.params.id,
              item: {
                ...this.draft.data,
                ...rest
              }
            })
          : Promise.resolve()
      ]);
      this.$router.push({ path: `/chains/${this.$route.params.id}` });
    },
    async confirmDelete() {
      const { id, name } = this.item;

      const confirmResult = await this.$confirm({
        title: 'Delete chain?',
        message: `Are you sure you want to delete '${name}'?\n\nThis action can't be undone.`,
        confirm: 'Delete'
      });

      if (!confirmResult) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('prompts/delete', id);

        this.$toast.success({
          title: 'Content chain deleted',
          message: `Content chain '${name}' was successfully deleted.`
        });
        this.$router.push({ path: `/chains` });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to content chain prompt',
          message: `Please, try again later or contact our development team.`
        });
      } finally {
        lock.release();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-prompt {
  display: grid;
  grid-gap: 0.5rem;
  grid-template-rows: minmax(0, max-content) minmax(0, 1fr);
  width: 100%;
  height: 100%;
  padding: 0 0.5rem 0 1rem;

  header {
    width: 100%;
    padding: 0.5rem 0.5rem 0 0;
    box-sizing: border-box;
    position: relative;
  }

  h1 {
    color: var(--theme-on-surface);
    font-size: 1.5rem;
    font-weight: 700;
  }

  .action-wrapper {
    position: absolute;
    right: 0rem;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
